import React, { useRef, useState } from 'react';
import { Box, Button, IconButton, Popover } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { currentMonthKey, formatMonthKey, shiftMonthKey } from '../utilities/helper/date.helper';
import { usePickerLocale } from '../i18n/usePickerLocale';
import { useTranslation } from 'react-i18next';

interface MonthStepperProps {
  /** `YYYY-MM`. */
  value: string;
  onChange: (month: string) => void;
  /** Latest selectable month; defaults to the current one. */
  max?: string;
  disabled?: boolean;
}

/**
 * Steps a month at a time, with the label itself opening a calendar for a longer jump.
 *
 * Forward is capped: there is no charge, payment or draft in the future, and an arrow that
 * always works but only ever yields four zeros reads as a broken screen rather than as an
 * empty month. Backwards is not capped - there is no point before which money did not exist.
 *
 * The calendar is a DateCalendar inside a Popover anchored to the button, rather than a
 * DatePicker. A DatePicker positions its popup against its own text field, so hiding that
 * field - which is the only way to get a label-triggered picker out of it - leaves the
 * popup with nothing to anchor to and it lands in the corner of the page. Owning the
 * anchor directly is both simpler and the reason this renders where it should.
 *
 * It carries its own LocalizationProvider because the app has no root one, exactly as
 * GenericTextField does.
 */
const MonthStepper: React.FC<MonthStepperProps> = ({ value, onChange, max, disabled }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const pickerLocale = usePickerLocale();
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const ceiling = max ?? currentMonthKey();
  const atCeiling = value >= ceiling;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={() => onChange(shiftMonthKey(value, -1))}
        aria-label={t('picker.previousMonth')}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Button
        ref={anchorRef}
        onClick={() => setPickerOpen(true)}
        disabled={disabled}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          minWidth: 170,
          fontFamily: 'Poppins',
          fontWeight: 500,
          color: 'text.primary'
        }}
      >
        {formatMonthKey(value)}
      </Button>

      <Popover
        open={pickerOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPickerOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: 4, mt: 1 } } }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={pickerLocale.adapterLocale} localeText={pickerLocale.localeText}>
          <DateCalendar
            views={['year', 'month']}
            openTo="month"
            value={dayjs(`${value}-01`)}
            maxDate={dayjs(`${ceiling}-01`)}
            onChange={next => {
              if (!next) return;
              onChange(next.format('YYYY-MM'));
              setPickerOpen(false);
            }}
            // the theme's subtitle1 is white (it is the sidebar's), and the month and year
            // buttons are set in it - without this only the selected one, white on primary,
            // can be read; GenericTextField does the same for its pickers
            slotProps={{
              monthButton: { sx: { color: 'text.primary' } },
              yearButton: { sx: { color: 'text.primary' } }
            }}
            sx={{
              // without a height the month grid is clipped to the day-view's height
              height: 'auto',
              '& .MuiPickersCalendarHeader-root': { pr: 1 }
            }}
          />
        </LocalizationProvider>
      </Popover>

      <IconButton
        size="small"
        disabled={disabled || atCeiling}
        onClick={() => onChange(shiftMonthKey(value, 1))}
        aria-label={t('picker.nextMonth')}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default MonthStepper;
